import React, { useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { string, bool, func, shape } from "prop-types";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import * as i18nUtils from "util/i18n";
import * as stringUtils from "util/string";
import { validateCUITWithRegex } from "util/form";
import { selectors as softTokenSelectors } from "reducers/softToken";
import { selectors as afipPaymentLinkSelectors, actions as afipPaymentLinkActions } from "reducers/afipPaymentLink";
import { selectors as sessionSelectors } from "reducers/session";
import CustomText from "pages/_components/CustomText/CustomText";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import I18n from "pages/_components/I18n";
import Credential from "pages/_components/fields/credentials/Credential";

const FORM_ID = "vep.addTaxpayer";

const AddTaxpayerForm = ({ deviceStatus, tokenInfo, handleClose, activeEnvironment, openForm }) => {
    const dispatch = useDispatch();
    const formRef = useRef();

    useEffect(() => {
        if (!openForm) {
            formRef.current.resetForm();
        }
    }, [openForm]);

    const closeForm = () => {
        formRef.current.resetForm();
        handleClose();
    };

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            cuit: "",
            reference: "",
            otp: "",
        },
        validationSchema: () =>
            Yup.object().shape({
                cuit: Yup.string()
                    .required(i18nUtils.get("global.error.required"))
                    .test("cuit", i18nUtils.get("vep.cuit.invalid.error", "Ingresá un CUIT/CUIL válido"), (value) =>
                        validateCUITWithRegex(value),
                    )
                    .test(
                        "cuit",
                        i18nUtils.get(
                            "vep.taxpayers.add.same.company.error",
                            "El CUIT/CUIL debe ser distinto al de tu empresa",
                        ),
                        (value) => activeEnvironment && value !== activeEnvironment?.activeEnvironmentDocument,
                    ),

                reference: Yup.string()
                    .required(i18nUtils.get("global.error.required"))
                    .test(
                        "reference",
                        i18nUtils.get("vep.taxpayers.add.reference.invalid", "La referencia debe ser alfanumérica"),
                        (value) => stringUtils.isAlphanumeric(value),
                    ),
                otp:
                    deviceStatus === "NO_DEVICE"
                        ? Yup.string().required(i18nUtils.get("global.error.required"))
                        : Yup.string().notRequired(),
            }),

        onSubmit: (values) => {
            if (values) {
                const { cuit, reference } = values;
                const _otp = deviceStatus === "NO_DEVICE" ? values.otp : tokenInfo.token;
                dispatch(afipPaymentLinkActions.addTaxpayer({ cuit, reference, _otp }));
                closeForm();
            }
        },
    };

    return (
        <div className="container centered">
            <h1 className="title-content form-drawer-title">
                <CustomText text={i18nUtils.get("vep.addTaxpayer.title", "Adherir contribuyente")} />
            </h1>
            <Formik {...formikProps} ref={formRef}>
                {() => (
                    <Form>
                        <div className="form-drawer-content">
                            <div className="pb-6" />
                            <I18n
                                id="vep.addTaxpayer.info"
                                defaultValue={i18nUtils.get(
                                    "vep.addTaxpayer.info",
                                    "Ingresá el CUIT del contribuyente con el que querés operar",
                                )}
                            />
                            <div className="pb-6" />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                idForm={FORM_ID}
                                maxLength={50}
                                name="cuit"
                                labelTextDefault={i18nUtils.get("vep.addTaxpayer.cuit", "CUIT/CUIL del contribuyente")}
                                hidePlaceholder
                            />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                idForm={FORM_ID}
                                maxLength={50}
                                name="reference"
                                labelTextDefault={i18nUtils.get("vep.taxpayer.reference", "Referencia")}
                                hidePlaceholder
                            />
                            {deviceStatus !== "NO_DEVICE" ? null : (
                                <Field
                                    idForm="form.credential"
                                    name="otp"
                                    type="otp"
                                    copyEnabled={false}
                                    component={Credential}
                                />
                            )}
                            <Button type="submit" bsStyle="primary" label="global.confirm" loading={false} block />
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

AddTaxpayerForm.propTypes = {
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
    deviceStatus: string.isRequired,
    tokenInfo: shape({}).isRequired,
    handleClose: func.isRequired,
    activeEnvironment: shape({}),
    openForm: bool.isRequired,
};

AddTaxpayerForm.defaultProps = {
    activeEnvironment: null,
};

const mapStateToProps = (state) => ({
    fetching: afipPaymentLinkSelectors.getFetching(state),
    tokenInfo: softTokenSelectors.getTokenInfo(state),
    deviceStatus: softTokenSelectors.getDeviceStatus(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default connect(mapStateToProps)(AddTaxpayerForm);
